import type { GridColDef, GridColumnGroup, GridColumnGroupingModel } from "@mui/x-data-grid-premium";
import type {
  ConditionConfiguration,
  MeasureDateRange,
} from "../../../../../../../api/types/fieldConfigurationTypes.ts";
import { formatDate } from "@entrilia/shared/utilities/dateUtils.ts";
import { defined } from "@entrilia/shared/utilities/typeHelper.ts";
import type {
  FinancialStatementGeneralSettings,
  FinancialStatementResponse,
  MetaDescriptorBase,
  StatementColumn,
  StatementDataColumnConfiguration,
  StatementHeaderCell,
  StatementLineConfiguration,
  StatementLineType,
  StatementPeriodScope,
  StatementRow,
  StatementRowKind,
  StatementValueCell,
} from "../../../../../../../api/types/svTypes.ts";
import { getValidatedDate } from "../../../../utils/dateUtils.ts";
import { getRelativeDateLabel, isRelativeDateValue } from "../../../../utils/relativeDates.ts";
import { dateRangeToShortString } from "../../../utilities/dateRangeToShortString.ts";
import { getColumnPeriodForLine } from "../../builder/financialStatement/utils/statementColumnPeriod.ts";
import { findPeriodAncestor, readsPeriodFromField } from "../../builder/financialStatement/utils/statementLines.ts";
import { getRangeText } from "../../builder/financialStatement/utils/statementOptions.ts";
import { getFormattedValue } from "../common/conditions/conditionsHelper.tsx";
import { getDisplayCaption } from "../common/fields/field-captions/captionsHelper.ts";
import { getDefaultColumnLabel } from "./statementColumnLabels.ts";

export const LABEL_COLUMN_FIELD = "__label";
const LABEL_COLUMN_WIDTH = 340;
const VALUE_COLUMN_WIDTH = 150;
const TOTAL_MEMBER_LABEL = "Total";
const GROUP_ID_PREFIX = "__tier";
const SHORT_DAY_FORMAT = "MMM d";
const MIXED_PERIOD_CODE = "Mixed";
const REPORTING_DATE_TEXT = "the reporting date";
const SELECTED_ROWS_SUFFIX = " on selected rows";
export const TOTAL_COLUMN_CLASS = "statement-column--total";

export type StatementRowVariant = "heading" | "amount" | "member" | "subtotal" | "total" | "formula" | "spacer";

export interface StatementGridRow {
  id: string;
  lineId: string;
  kind: StatementRowKind;
  label: string;
  level: number;
  variant: StatementRowVariant;
  cells: Record<string, StatementValueCell>;
  isToggle: boolean;
  isCollapsed: boolean;
  isGrandTotal: boolean;
}

export interface StatementColumnHeader {
  label: string;
  tooltip?: string;
  periodCode?: string;
}

export type StatementColumnPeriod = Pick<
  StatementDataColumnConfiguration,
  "dateRange" | "periodScope" | "periodLineGuids"
>;

export type StatementGridColumn = GridColDef<StatementGridRow> & {
  header?: StatementColumnHeader;
  columnPeriod?: StatementColumnPeriod;
};

export type LinePeriodSource = { kind: "line" } | { kind: "inherited"; lineLabel: string } | { kind: "default" };

export interface LinePeriod {
  range: MeasureDateRange;
  source: LinePeriodSource;
}

export type CellPeriodSource =
  | { kind: "column"; selectedRowsOnly: boolean; overriddenLineRange?: MeasureDateRange }
  | (LinePeriodSource & { skippedColumnRange?: MeasureDateRange });

export interface CellPeriod {
  range: MeasureDateRange;
  source: CellPeriodSource;
}

export interface StatementGridColumns {
  columns: StatementGridColumn[];
  columnGroupingModel: GridColumnGroupingModel;
  pinnedLeft: string[];
}

export interface ResolvedColumnLabel {
  text: string;
  unknownTokens: string[];
}

export type GetFieldCaption = (fieldName: string) => string;

interface LabelToken {
  name: string;
  text: string;
}

interface HeaderSpan {
  cell: StatementHeaderCell;
  position: number;
  start: number;
  end: number;
}

interface HeaderContext {
  dataColumnsById: Map<string, StatementDataColumnConfiguration>;
  conditions: ConditionConfiguration[];
  settings: FinancialStatementGeneralSettings;
  rowsPeriod: MeasureDateRange | undefined;
  getFieldCaption: GetFieldCaption;
}

interface LineResponseRows {
  line?: StatementRow;
  members: StatementRow[];
}

interface RowContext {
  lines: StatementLineConfiguration[];
  rowsByLine: Map<string, LineResponseRows>;
  collapsed: ReadonlySet<string>;
  getFieldCaption: GetFieldCaption;
}

const defaultLinePeriodSource: LinePeriodSource = { kind: "default" };

const isoDatePattern = /^\d{4}-\d{2}-\d{2}(T\S*)?$/;
const tokenNamePattern = /^\p{L}[\p{L}\p{N} ]*/u;

const variantByLineType: Record<StatementLineType, StatementRowVariant> = {
  Group: "heading",
  Amount: "amount",
  Dimension: "heading",
  Subtotal: "subtotal",
  Total: "total",
  Formula: "formula",
  Spacer: "spacer",
};

const labelColumn: StatementGridColumn = {
  field: LABEL_COLUMN_FIELD,
  headerName: "",
  width: LABEL_COLUMN_WIDTH,
  sortable: false,
  disableColumnMenu: true,
  disableReorder: true,
};

export const isRedNegative = (
  cell: Pick<StatementValueCell, "value"> | undefined,
  settings: Pick<FinancialStatementGeneralSettings, "showNegativesInRed">
): boolean => settings.showNegativesInRed && (cell?.value ?? 0) < 0;

export const periodCode = (
  cell: Pick<StatementValueCell, "dateRange"> | undefined,
  settings: FinancialStatementGeneralSettings
): string | undefined =>
  settings.highlightPeriod && cell?.dateRange ? dateRangeToShortString(cell.dateRange) || undefined : undefined;

export const createFieldCaptionLookup = (fields: Pick<MetaDescriptorBase, "name" | "caption">[]): GetFieldCaption => {
  const captions = new Map(fields.map((field) => [field.name, field.caption]));
  return (fieldName) => captions.get(fieldName) ?? fieldName;
};

const isDateValue = (value: string | null): boolean =>
  value !== null && (isRelativeDateValue(value) || isoDatePattern.test(value));

const toCalendarDate = (value: string | null): Date | undefined => {
  const date = value === null ? undefined : getValidatedDate(value);
  return date && !Number.isNaN(date.getTime()) ? date : undefined;
};

const formatDateValue = (value: string | null): string => {
  if (value !== null && isRelativeDateValue(value)) {
    return getRelativeDateLabel(value);
  }
  const date = toCalendarDate(value);
  return date ? formatDate(date) : (value ?? "");
};

const formatDateRange = (from: Date, to: Date): string => {
  if (from.getTime() === to.getTime()) {
    return formatDate(to);
  }
  const fromText = from.getFullYear() === to.getFullYear() ? formatDate(from, SHORT_DAY_FORMAT) : formatDate(from);
  return `${fromText} – ${formatDate(to)}`;
};

const formatDateValues = (values: (string | null)[]): string => {
  const [from, to] = values.map(toCalendarDate);
  if (values.length === 2 && from && to) {
    return formatDateRange(from, to);
  }
  return values.map(formatDateValue).join(values.length === 2 ? " – " : ", ");
};

const formatConditionValues = (values: (string | null)[]): string =>
  values.length > 0 && values.every(isDateValue) ? formatDateValues(values) : values.map(getFormattedValue).join(", ");

const toLabelToken = (condition: ConditionConfiguration, getFieldCaption: GetFieldCaption): LabelToken => ({
  name: getDisplayCaption(
    getFieldCaption(condition.filter.dimensionName),
    condition.customLabel,
    condition.systemLabel
  ),
  text: formatConditionValues(condition.filter.values),
});

const resolveSegment = (segment: string, tokens: LabelToken[]): { text: string; unknownToken?: string } => {
  const token = tokens.find(({ name }) => segment.slice(0, name.length).toLowerCase() === name.toLowerCase());
  if (token) {
    return { text: `${token.text || token.name}${segment.slice(token.name.length)}` };
  }
  return { text: `@${segment}`, unknownToken: tokenNamePattern.exec(segment)?.[0].trim() };
};

export const resolveColumnLabel = (
  label: string,
  conditions: ConditionConfiguration[],
  getFieldCaption: GetFieldCaption
): ResolvedColumnLabel => {
  const tokens = conditions
    .map((condition) => toLabelToken(condition, getFieldCaption))
    .filter((token) => token.name !== "")
    .sort((first, second) => second.name.length - first.name.length);
  const [head = "", ...segments] = label.split("@");
  const resolved = segments.map((segment) => resolveSegment(segment, tokens));
  return {
    text: head + resolved.map((segment) => segment.text).join(""),
    unknownTokens: resolved.flatMap((segment) => (segment.unknownToken ? [segment.unknownToken] : [])),
  };
};

const hasColumnDate = (dataColumn: StatementDataColumnConfiguration): boolean =>
  dataColumn.linkedConditions.length > 0 ||
  dataColumn.customConditions.some((condition) => condition.filter.values.length > 0);

const getColumnDateLabel = (
  column: StatementColumn,
  dataColumn: StatementDataColumnConfiguration | undefined
): string => {
  if (column.periodCaption) {
    return column.periodCaption;
  }
  return dataColumn && hasColumnDate(dataColumn) ? formatDateValues(column.dateValues) : "";
};

const getColumnReportConditions = (
  column: StatementColumn,
  dataColumn: StatementDataColumnConfiguration,
  conditions: ConditionConfiguration[]
): ConditionConfiguration[] =>
  conditions.map((condition) =>
    dataColumn.linkedConditions.includes(condition.guid)
      ? { ...condition, filter: { ...condition.filter, values: column.dateValues } }
      : condition
  );

const getColumnName = (
  column: StatementColumn,
  dataColumn: StatementDataColumnConfiguration | undefined,
  context: HeaderContext
): string => {
  const customLabel = dataColumn?.customLabel?.trim();
  if (!dataColumn || !customLabel) {
    return "";
  }
  const reportConditions = getColumnReportConditions(column, dataColumn, context.conditions);
  const conditions = [...dataColumn.customConditions, ...reportConditions];
  return resolveColumnLabel(customLabel, conditions, context.getFieldCaption).text;
};

const getColumnTooltip = (
  column: StatementColumn,
  name: string,
  dateLabel: string,
  periodScope: StatementPeriodScope | undefined
): string => {
  const text =
    name && dateLabel && name !== dateLabel
      ? `${name} — read at ${dateLabel}`
      : `Read at ${dateLabel || formatDateValues(column.dateValues) || REPORTING_DATE_TEXT}`;
  if (!column.dateRange) {
    return text;
  }
  return `${text}, ${getRangeText(column.dateRange)}${periodScope === "Selected" ? SELECTED_ROWS_SUFFIX : ""}`;
};

const getSharedRowsPeriod = (
  linePeriods: ReadonlyMap<string, LinePeriod>,
  defaultRange: MeasureDateRange
): MeasureDateRange | undefined => {
  const ranges = [...new Set([...linePeriods.values()].map(({ range }) => range))];
  return ranges.length > 1 ? undefined : (ranges[0] ?? defaultRange);
};

const getColumnPeriodCode = (
  column: StatementColumn,
  periodScope: StatementPeriodScope | undefined,
  context: HeaderContext
): string | undefined => {
  if (!context.settings.highlightPeriod || column.isFormula) {
    return undefined;
  }
  if (column.dateRange && periodScope !== "Selected") {
    return dateRangeToShortString(column.dateRange) || undefined;
  }
  const { rowsPeriod } = context;
  if (rowsPeriod === undefined || (column.dateRange && column.dateRange !== rowsPeriod)) {
    return MIXED_PERIOD_CODE;
  }
  return dateRangeToShortString(rowsPeriod) || undefined;
};

const buildColumnHeader = (column: StatementColumn, context: HeaderContext): StatementColumnHeader => {
  const dataColumn = context.dataColumnsById.get(column.dataColumnId);
  const name = getColumnName(column, dataColumn, context);
  const dateLabel = getColumnDateLabel(column, dataColumn);
  return {
    label: name || dateLabel || getDefaultColumnLabel(column.isFormula),
    tooltip: column.isFormula ? undefined : getColumnTooltip(column, name, dateLabel, dataColumn?.periodScope),
    periodCode: getColumnPeriodCode(column, dataColumn?.periodScope, context),
  };
};

const toColumnPeriod = (
  column: StatementColumn,
  dataColumn: StatementDataColumnConfiguration | undefined
): StatementColumnPeriod | undefined =>
  column.dateRange
    ? {
        dateRange: column.dateRange,
        periodScope: dataColumn?.periodScope,
        periodLineGuids: dataColumn?.periodLineGuids,
      }
    : undefined;

const toValueColumn = (
  column: StatementColumn,
  header: StatementColumnHeader,
  dataColumn: StatementDataColumnConfiguration | undefined
): StatementGridColumn => ({
  field: column.key,
  headerName: header.label,
  width: VALUE_COLUMN_WIDTH,
  align: "right",
  headerAlign: "right",
  sortable: false,
  disableColumnMenu: true,
  disableReorder: true,
  cellClassName: column.isTotal ? TOTAL_COLUMN_CLASS : undefined,
  headerClassName: column.isTotal ? TOTAL_COLUMN_CLASS : undefined,
  header,
  columnPeriod: toColumnPeriod(column, dataColumn),
});

const toHeaderSpans = (cells: StatementHeaderCell[]): HeaderSpan[] =>
  cells.reduce<HeaderSpan[]>((spans, cell, position) => {
    const start = spans.at(-1)?.end ?? 0;
    spans.push({ cell, position, start, end: start + cell.span });
    return spans;
  }, []);

const buildColumnGroups = (
  tiers: HeaderSpan[][],
  columns: StatementColumn[],
  tierIndex: number,
  start: number,
  end: number
): GridColumnGroup[] =>
  (tiers[tierIndex] ?? [])
    .filter((span) => span.start >= start && span.end <= end)
    .map((span): GridColumnGroup => ({
      groupId: `${GROUP_ID_PREFIX}${tierIndex}-${span.position}`,
      headerName: span.cell.isTotal ? TOTAL_MEMBER_LABEL : (span.cell.caption ?? span.cell.memberKey ?? ""),
      headerAlign: "right",
      headerClassName: span.cell.isTotal ? TOTAL_COLUMN_CLASS : undefined,
      children:
        tierIndex + 1 < tiers.length
          ? buildColumnGroups(tiers, columns, tierIndex + 1, span.start, span.end)
          : columns.slice(span.start, span.end).map((column) => ({ field: column.key })),
    }));

export const buildStatementColumns = (
  response: FinancialStatementResponse,
  dataColumns: StatementDataColumnConfiguration[],
  conditions: ConditionConfiguration[],
  settings: FinancialStatementGeneralSettings,
  linePeriods: ReadonlyMap<string, LinePeriod>,
  getFieldCaption: GetFieldCaption
): StatementGridColumns => {
  const context: HeaderContext = {
    dataColumnsById: new Map(dataColumns.map((dataColumn) => [dataColumn.guid, dataColumn])),
    conditions,
    settings,
    rowsPeriod: getSharedRowsPeriod(linePeriods, settings.defaultDateRange),
    getFieldCaption,
  };
  const tiers = response.headerRows.map((headerRow) => toHeaderSpans(headerRow.cells));
  return {
    columns: [
      labelColumn,
      ...response.columns.map((column) =>
        toValueColumn(column, buildColumnHeader(column, context), context.dataColumnsById.get(column.dataColumnId))
      ),
    ],
    columnGroupingModel: buildColumnGroups(tiers, response.columns, 0, 0, response.columns.length),
    pinnedLeft: [LABEL_COLUMN_FIELD],
  };
};

const groupRowsByLine = (rows: StatementRow[]): Map<string, LineResponseRows> => {
  const rowsByLine = new Map<string, LineResponseRows>();
  rows.forEach((row) => {
    const lineRows = rowsByLine.get(row.lineId) ?? { members: [] };
    if (row.kind === "Member") {
      lineRows.members.push(row);
    } else {
      lineRows.line = row;
    }
    rowsByLine.set(row.lineId, lineRows);
  });
  return rowsByLine;
};

export const getLineLabel = (line: StatementLineConfiguration, getFieldCaption: GetFieldCaption): string => {
  if (line.customLabel) {
    return line.customLabel;
  }
  const field = line.lineType === "Dimension" ? line.dimension : line.field;
  return field ? getFieldCaption(field.name) : "";
};

const withLinePeriod = (line: StatementLineConfiguration): StatementLineConfiguration =>
  readsPeriodFromField(line.lineType) ? { ...line, dateRange: line.field?.dateRange ?? undefined } : line;

const getLinePeriod = (
  lines: StatementLineConfiguration[],
  line: StatementLineConfiguration,
  defaultRange: MeasureDateRange,
  getFieldCaption: GetFieldCaption
): LinePeriod => {
  if (line.dateRange) {
    return { range: line.dateRange, source: { kind: "line" } };
  }
  const ancestor = findPeriodAncestor(lines, line.guid);
  if (ancestor?.dateRange) {
    return {
      range: ancestor.dateRange,
      source: { kind: "inherited", lineLabel: getLineLabel(ancestor, getFieldCaption) },
    };
  }
  return { range: defaultRange, source: { kind: "default" } };
};

export const resolveStatementLinePeriods = (
  lines: StatementLineConfiguration[],
  defaultRange: MeasureDateRange,
  getFieldCaption: GetFieldCaption
): ReadonlyMap<string, LinePeriod> => {
  const periodLines = lines.map(withLinePeriod);
  return new Map(
    periodLines
      .filter((line) => readsPeriodFromField(line.lineType))
      .map((line) => [line.guid, getLinePeriod(periodLines, line, defaultRange, getFieldCaption)])
  );
};

export const getCellPeriod = (
  cellRange: MeasureDateRange,
  linePeriod: LinePeriod | undefined,
  columnPeriod: StatementColumnPeriod | undefined,
  lines: StatementLineConfiguration[],
  lineGuid: string
): CellPeriod => {
  const columnRange = columnPeriod && getColumnPeriodForLine(columnPeriod, lines, lineGuid);
  if (columnRange) {
    const overriddenLineRange = linePeriod && linePeriod.range !== columnRange ? linePeriod.range : undefined;
    const selectedRowsOnly = columnPeriod?.periodScope === "Selected";
    return { range: cellRange, source: { kind: "column", selectedRowsOnly, overriddenLineRange } };
  }
  const lineSource = linePeriod?.source ?? defaultLinePeriodSource;
  return { range: cellRange, source: { ...lineSource, skippedColumnRange: columnPeriod?.dateRange } };
};

const getLinePeriodSourceText = (source: LinePeriodSource): string => {
  switch (source.kind) {
    case "line":
      return "set on this line";
    case "inherited":
      return `inherited from “${source.lineLabel}”`;
    case "default":
      return "report default";
  }
};

const getCellPeriodSourceText = ({ source }: CellPeriod): string => {
  if (source.kind === "column") {
    const columnText = `set on this data column${source.selectedRowsOnly ? " for selected rows" : ""}`;
    return source.overriddenLineRange
      ? `${columnText}, overriding the line's ${getRangeText(source.overriddenLineRange)}`
      : columnText;
  }
  const lineText = getLinePeriodSourceText(source);
  return source.skippedColumnRange
    ? `${lineText} (this row isn't selected for the column's ${getRangeText(source.skippedColumnRange)})`
    : lineText;
};

export const getCellPeriodDescription = (period: CellPeriod): string =>
  `Period: ${getRangeText(period.range)} — ${getCellPeriodSourceText(period)}`;

const createLineRow = (
  line: StatementLineConfiguration,
  label: string,
  level: number,
  cells: Record<string, StatementValueCell>
): StatementGridRow => ({
  id: line.guid,
  lineId: line.guid,
  kind: "Line",
  label,
  level,
  variant: variantByLineType[line.lineType],
  cells,
  isToggle: false,
  isCollapsed: false,
  isGrandTotal: line.grandTotalStyle,
});

const createMemberRow = (line: StatementLineConfiguration, member: StatementRow, level: number): StatementGridRow => {
  const memberKey = member.memberKey ?? "";
  return {
    id: `${line.guid}:member:${memberKey}`,
    lineId: line.guid,
    kind: "Member",
    label: member.memberCaption ?? memberKey,
    level,
    variant: "member",
    cells: member.cells,
    isToggle: false,
    isCollapsed: false,
    isGrandTotal: false,
  };
};

const buildDimensionRows = (
  context: RowContext,
  line: StatementLineConfiguration,
  lineRow: StatementRow,
  members: StatementRow[],
  level: number
): StatementGridRow[] => {
  const label = getLineLabel(line, context.getFieldCaption);
  const memberRows = members.map((member) => createMemberRow(line, member, line.showAsGroup ? level + 1 : level));
  const totalRows: StatementGridRow[] = line.showSubtotal
    ? [
        {
          ...createLineRow(line, `Total ${label}`, level, lineRow.cells),
          id: `${line.guid}:total`,
          variant: "subtotal",
        },
      ]
    : [];
  if (!line.showAsGroup) {
    return [...memberRows, ...totalRows];
  }
  const isToggle = memberRows.length > 0;
  const isCollapsed = isToggle && context.collapsed.has(line.guid);
  const heading = { ...createLineRow(line, label, level, isCollapsed ? lineRow.cells : {}), isToggle, isCollapsed };
  return isCollapsed ? [heading] : [heading, ...memberRows, ...totalRows];
};

const buildLineRows = (context: RowContext, line: StatementLineConfiguration, level: number): StatementGridRow[] => {
  const lineRows = context.rowsByLine.get(line.guid);
  if (!lineRows?.line) {
    return [];
  }
  if (line.lineType === "Dimension") {
    return buildDimensionRows(context, line, lineRows.line, lineRows.members, level);
  }
  const label = line.lineType === "Spacer" ? "" : getLineLabel(line, context.getFieldCaption);
  return [createLineRow(line, label, level, lineRows.line.cells)];
};

const getDescendantsEnd = (lines: StatementLineConfiguration[], index: number): number => {
  const level = defined(lines[index]).level;
  const end = lines.findIndex((line, lineIndex) => lineIndex > index && line.level <= level);
  return end === -1 ? lines.length : end;
};

const findTrailingTotal = (
  context: RowContext,
  group: StatementLineConfiguration,
  index: number,
  childGuids: ReadonlySet<string>
): { line: StatementLineConfiguration; row: StatementRow } | undefined => {
  const candidate = context.lines[index];
  if (candidate?.level !== group.level || (candidate.lineType !== "Subtotal" && candidate.lineType !== "Total")) {
    return undefined;
  }
  if (candidate.lineType === "Total" && !candidate.lineIds.every((guid) => childGuids.has(guid))) {
    return undefined;
  }
  const row = context.rowsByLine.get(candidate.guid)?.line;
  return row ? { line: candidate, row } : undefined;
};

const buildRows = (context: RowContext, start: number, end: number, flattenedAncestors: number): StatementGridRow[] => {
  const rows: StatementGridRow[] = [];
  let index = start;
  while (index < end) {
    const line = defined(context.lines[index]);
    const level = line.level - flattenedAncestors;
    if (line.lineType !== "Group") {
      rows.push(...buildLineRows(context, line, level));
      index += 1;
      continue;
    }

    const childrenEnd = getDescendantsEnd(context.lines, index);
    const childGuids = new Set(context.lines.slice(index + 1, childrenEnd).map((child) => child.guid));
    const children = buildRows(context, index + 1, childrenEnd, flattenedAncestors + (line.showAsGroup ? 0 : 1));
    index = childrenEnd;
    if (!line.showAsGroup || !context.rowsByLine.get(line.guid)?.line) {
      rows.push(...children);
      continue;
    }

    const isToggle = children.length > 0;
    const heading = { ...createLineRow(line, getLineLabel(line, context.getFieldCaption), level, {}), isToggle };
    if (!isToggle || !context.collapsed.has(line.guid)) {
      rows.push(heading, ...children);
      continue;
    }

    const trailingTotal = findTrailingTotal(context, line, childrenEnd, childGuids);
    rows.push({
      ...heading,
      isCollapsed: true,
      cells: trailingTotal?.row.cells ?? {},
      isGrandTotal: trailingTotal?.line.grandTotalStyle ?? heading.isGrandTotal,
    });
    if (trailingTotal) {
      index += 1;
    }
  }
  return rows;
};

export const buildStatementRows = (
  response: FinancialStatementResponse,
  lines: StatementLineConfiguration[],
  collapsed: ReadonlySet<string>,
  getFieldCaption: GetFieldCaption
): StatementGridRow[] => {
  const context: RowContext = { lines, rowsByLine: groupRowsByLine(response.rows), collapsed, getFieldCaption };
  return buildRows(context, 0, lines.length, 0);
};
